import { observable, action, toJS } from "mobx";
import moment from 'moment';
import * as api from '../services/api';
import PrintStore from './PrintStore';

class FactoryStore {
    @observable loading = false;
    @observable productionDate = moment();
    @observable dateRange = [null, null];
    @observable productionBatches = null;
    @observable selectedSkusToPrint = null;
    @observable skus = null;
    @observable unitSkus;
    @observable selectedUnitSku;
    @observable selectedUnitSkuName;
    @observable productionDate = moment().format('DD-MM-YYYY');
    @observable expirationTime = null;
    @observable uomQuantity = null;
    @observable expirationDate = null;

    @action
    handleGetUnitSkus = (callback) => {
        api.getUnitSkus()
            .then(res => {
                this.unitSkus = res
                    .filter(u => u.packaging && u.packaging.length > 0)
                    .map(u => {
                        return { value: u.id, label: u.sku, subComponent: u.sub_component, name : u.name };
                    });
                if (callback) {
                    callback();
                }
            })
            .catch((error) => console.log(error));
    };

    @action setSelectedUnitSku = (unitSku) => {
        this.selectedUnitSku = unitSku;
        this.selectedUnitSkuName = unitSku.name;
        api.getComponentDetails(unitSku.label)
        .then(res => {
            this.expirationTime = res.expiration_time;
            this.uomQuantity = res.uom_quantity
        })
        .catch(err => console.log(err));
    };

    @action handleSetDate = (date) => {
        this.productionDate = date;
    }

    @action handleExpiryDate = (date) => {
        this.expirationDate = date;
    }

    @action handleGetSkus = () => {
        this.loading = true;
        api.getSkus()
        .then(res => {
            this.skus = res;
        })
        .then(() => this.handleGetProductionBatches())
        .catch(err => console.log(err));
    }

    getPalletCountPerSku = (mealLetter, sku) => {
        return this.productionBatches[mealLetter][sku].totalPallets;
    }

    @action handleSetProductionDate = (productionDate) => {
        this.productionDate = productionDate;
    }

    @action handleSetDateRange = (dateRange) => {
        this.dateRange = dateRange;
    }

    @action handleGetProductionBatches = () => {
        const from_date = moment(this.dateRange[0]).format('DD-MM-YYYY');
        const to_date = moment(this.dateRange[1]).add(1, 'days').format('DD-MM-YYYY')

        api.getProductionBatches(from_date, to_date)
            .then(res => {
                this.productionBatches = res.batches.reduce((acc, cur) => {
                    const {
                        color: sku,
                        sourced_quantity,
                    } = cur;
                    
                    if (sourced_quantity) {
                        const mealLetter = sku.charAt(0);
                        const skuData = this.skus.find(item => item.sku === sku);
                        const { component_names, meals_per_pallet } = skuData

                        const mealComponentParts = component_names ? component_names.split(' ') : [];
                        const breakpoint = Math.ceil(mealComponentParts.length / 2);
                        const mealComponentLine1 = mealComponentParts.slice(0, breakpoint).join(' ');
                        const mealComponentLine2 = mealComponentParts.slice(breakpoint).join(' ');

                        const totalPallets = meals_per_pallet ? Math.ceil(sourced_quantity / meals_per_pallet) : 0;

                        acc[mealLetter] = acc[mealLetter] || {};
                        acc[mealLetter][sku] = acc[mealLetter][sku] || cur;
            
                        acc[mealLetter][sku]['totalPallets'] = totalPallets;
                        acc[mealLetter][sku]['mealComponentLine1'] = mealComponentLine1;
                        acc[mealLetter][sku]['mealComponentLine2'] = mealComponentLine2;
                    }

                    return acc;
                }, {});
        
                this.selectedSkusToPrint = res.batches.reduce((acc, cur) =>{
                    const { color: sku, sourced_quantity } = cur;
                    const mealLetter = sku.charAt(0);

                    if (sourced_quantity) {
                        acc[mealLetter] = acc[mealLetter] ? acc[mealLetter].concat([sku]) : [sku];
                    }

                    return acc;
                }, {})
        
                this.loading = false;
            })
            .catch(err => console.log(err));
    }

    @action setSelectedSkusToPrint = (mealLetter, selectedSku) => {
        if (this.selectedSkusToPrint[mealLetter].includes(selectedSku)) {
          this.selectedSkusToPrint[mealLetter] = this.selectedSkusToPrint[mealLetter].filter(sku => sku !== selectedSku);
        } else {
          this.selectedSkusToPrint[mealLetter].push(selectedSku);
          this.selectedSkusToPrint[mealLetter].sort();
        }
    }

    getFactoryLabelPrintString = ({
        batchDate,
        mealComponentLine1,
        mealComponentLine2,
        mealComponentLine3,
        productionDate,
        sku,
    }) => {
        return `
        ^XA
        ^FX Middle Divider
        ^FO390,2^GB0,200,3^FS
        ^FX Horizontal Dividers
        ^FO0,70^GB850,0,3^FS
        ^FO0,135^GB850,0,3^FS
        ^FO0,200^GB850,0,3^FS
        ^FT45,55^A0N,40,40^FB400,1,0,L^FH\\^FDPRODUCTION DATE^FS
        ^FT45,120^A0N,40,40^FB400,1,0,L^FH\\^FDEXPIRY DATE^FS
        ^FT45,180^A0N,40,40^FB400,1,0,L^FH\\^FDCOMPONENT CODE^FS
            
        ^FT405,55^A0N,40,40^FB400,1,0,L^FH\\^FD${productionDate}^FS
        ^FT405,120^A0N,40,40^FB400,1,0,L^FH\\^FD${batchDate}^FS
        ^FT405,180^A0N,40,40^FB500,1,0,L^FH\\^FD${sku.replace(/_/g, '-')}^FS
        ^FT130,250^A0N,40,40^FB600,1,0,C^FH\\^FD${mealComponentLine1}^FS
        ^FT130,300^A0N,40,40^FB600,1,0,C^FH\\^FD${mealComponentLine2}^FS
        ^FT130,350^A0N,40,40^FB600,1,0,C^FH\\^FD${mealComponentLine3}^FS
                
        ^XZ`
    }

    sanitizeZplText = (text = '') => {
        return String(text)
            .replace(/\^/g, '')
            .replace(/~/g, '')
            .replace(/\r?\n/g, ' ')
            .trim();
    }

    formatHalalLabelDate = (date) => {
        if (!date) {
            return '-';
        }

        const parsedDate = moment(date, ['DD-MM-YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
        if (parsedDate.isValid()) {
            return parsedDate.format('DD-MM-YYYY');
        }

        return String(date).toUpperCase();
    }

    getManualFactoryLabelPrintString = (printData = {}) => {
        const productionDate = this.sanitizeZplText(this.productionDate || '-');
        const expiryDate = this.sanitizeZplText(this.expirationDate || '-');
        const uomQuantity = this.sanitizeZplText(this.uomQuantity || '-');
        const componentCode = this.sanitizeZplText(
            this.selectedUnitSku && this.selectedUnitSku.label
                ? this.selectedUnitSku.label.replace(/_/g, '-')
                : '-'
        );
        const productName = this.sanitizeZplText(
            this.selectedUnitSkuName
                || (this.selectedUnitSku && this.selectedUnitSku.name)
                || '-'
        );

        return `
        ^XA
        ^CI28
        ^LL320
        ^LH12,0
        ^LS0
        ^LT0
        ^FX --- Middle Divider (Centered at 320) ---
        ^FO320,2^GB0,265,3^FS
        ^FX --- Horizontal Dividers (Shortened to 640) ---
        ^FO0,70^GB640,0,3^FS
        ^FO0,135^GB640,0,3^FS
        ^FO0,200^GB640,0,3^FS
        ^FO0,265^GB640,0,3^FS
        ^FX --- Left Column Labels ---
        ^FT16,55^A0N,28,28^FB288,1,0,L^FDPRODUCTION DATE^FS
        ^FT16,120^A0N,28,28^FB288,1,0,L^FDEXPIRY DATE^FS
        ^FT16,180^A0N,28,28^FB288,1,0,L^FDUOM QUANTITY^FS
        ^FT16,240^A0N,28,28^FB288,1,0,L^FDCOMPONENT CODE^FS
        ^FX --- Right Column Data ---
        ^FT336,55^A0N,28,28^FB288,1,0,L^FD${productionDate}^FS
        ^FT336,120^A0N,28,28^FB288,1,0,L^FD${expiryDate}^FS
        ^FT336,180^A0N,28,28^FB288,1,0,L^FD${uomQuantity}^FS
        ^FT336,240^A0N,28,28^FB288,1,0,L^FD${componentCode}^FS
        ^FX --- Bottom Product Name (Centered across 640) ---
        ^FT0,310^A0N,36,36^FB640,1,0,C^FD${productName}^FS
        ^XZ`
    }

    getManualFactoryHalalLabelPrintString = (printData = {}) => {
        const productName = this.sanitizeZplText((this.selectedUnitSkuName || '').toUpperCase());
        const productNameBm = this.sanitizeZplText(printData.productNameBm || '');
        const groupNumber = this.sanitizeZplText(
            this.selectedUnitSku && this.selectedUnitSku.label
                ? this.selectedUnitSku.label.replace(/_/g, '-')
                : '-'
        );
        const productionDate = this.sanitizeZplText(this.formatHalalLabelDate(this.productionDate));
        const expiryDate = this.sanitizeZplText(this.formatHalalLabelDate(this.expirationDate));
        const uomQuantity = this.sanitizeZplText(this.uomQuantity || '-');

        return `
        ^XA
        ^CI28
        ^LL320
        ^LH12,0
        ^LS0
        ^LT0
        ^FO12,23^GFA,4875,4875,25,0000000000000000000003FFFFF8000000000000000000000000000000000000000000FFFFFFFFE0000000000000000000000000000000000000001FFFFFFFFFFF00000000000000000000000000000000000001FFFFFFFFFFFFF000000000000000000000000000000000000FFFFFFFFFFFFFFE00000000000000000000000000000000007FFFFF00001FFFFFC000000000000000000000000000000003FFFF800000003FFFF80000000000000000000000000000000FFFF00000000001FFFE0000000000000000000000000000007FFF0000000000001FFFC00000000000000000000000000001FFF800060000000003FFF00000000000000000000000000007FFC0000600000078007FFC000000000000000000000000001FFF00000700000039800FFF000000000000000000000000003FF800000F00000039C003FF80000000000000000000000000FFE000000B8000001B0000FFE0000000000000000000000003FF8000000B8000001E00003FF8000000000000000000000007FE0000000FC000001C00000FFC00000000000000000000001FF800000019C000001C000003FF00000000000000000000003FE000000011E0000018000000FF8000000000000000000000FF8000000039000000380000003FE000000000000000000001FF0200000000000000780000001FF000000000000000000003FC0E000000000000000400000007F80000000000000000000FF81E00000000FFFFE00000000003FE0000000000000000001FE0070000003FFFFFFF8000000000FF0000000000000000003FC007000003FFFFFFFFF8000000F07F8000000000000000007F800388003FFFFFFFFFFF800001D83FC00000000000000000FF0003CC01FFFFFFFFFFFFF00001881FE00000000000000001FC0001F807FFFFFFBFFFFFFC0001C807F00000000000000003F80000E03FFFFFFF1FFFFFFF8001C003F80000000000000007F0000180FFFFFFFE0FFFFFFFE008E001FC000000000000000FE0000003FFFFFFFC07FFFFFFF808E000FE000000000000001FC000000FFFFFFFF803FFFFFFFE0CE0007F000000000000003F8000001FFFFFFFF001FFFFFFFF07C0003F800000000000007F0000007FFFFFFFE000FFFFFFFFC000001FC0000000000000FE000001FFFFFFFFC0007FFFFFFFF000000FE0000000000001FC000003FFFFFFFF80003FFFFFFFF8000007F0000000000003F8000007FFFFFFFF00001FFFFFFFFC000003F8000000000007F000001FFFFFFFFE00000FFFFFFFFF000001FC000000000007E000003FFFFFFFFC000007FFFFFFFF800000FC00000000000FC000007FFFFFFFF8000003FFFFFFFFC000007E00000000001FC00001FFFFFFFFF0000001FFFFFFFFF000003F00000000003F8F0003FFFFFFFFE0000000FFFFFFFFF800003F80000000003F07C007FFFFFFFFC00000007FFFFFFFFC00001F80000000007E07F80FFFFFFFFF800000003FFFFFFFFE00000FC000000000FC02FC1FFFFFFFFF000000001FFFFFFFFF000087E000000000FC03D83FFFFFFFFE000000000FFFFFFFFF8001C7E000000001F801907FFFFFFFFC0000000007FFFFFFFFC003E3F000000003F00180FFFFFFFFF80000000003FFFFFFFFE00F81F800000003F000C1FE0000000000000000000000000FF01F01F800000007E000C1FE0000000000000000000000000FF07C00FC00000007C00083FE0000000000000000000000000FF878007C0000000FC00007FE0000000000000000000000000FFC30007E0000001F80000FFE0000000000000000000000000FFE10003F0000001F80000FFE0000000000000000000000000FFE00003F0000003F00001FFE0000000000000000000000000FFF00001F8000003F00003FFE0000000000000000000000000FFF80001F8000007E00007FFE0000000000000000000000000FFFC0000FC000007E00007FFE0000000000000000000000000FFFC0000FC000007C0000FFFE0000000000000000000000000FFFE00007E00000FC0000FFFE0000000000000000000000000FFFE00007E00000F80001FFFE0000000000000000000000000FFFF00003E00001F80003FFFE0000000000000000000000000FFFF80003F00001F00003FFFE0000010000000000000000000FFFF80001F00003F00007FFFE0000010000000000000000000FFFFC0001F80003F00007FFFE0000038000000000000000000FFFFC0000F80003E0000FFFFE0000038000000000000000000FFFFE0000F80007E0000FFFFE0000078000000000000000000FFFFE0000FC0007C0001FFFFE00000FC000000000000000000FFFFF00007C0007C4001FFFFE00000FE000000000000000000FFFFF00007C000FC7801FFFFE00000FF000000000000000000FFFFF00007E000F8FFE3FFFFE000007F000000040000000000FFFFF80003E000F87FC3FFFFE000007F0000000C0000000000FFFFF80003E001F811C7FFFFE000007E0000000E0000000000FFFFFC4003F001F00C47FFFFE000007E0000000E0000000000FFFFFC7E01F001F00E07FFFFE000007E0000000E0000000000FFFFFC6FE1F001F1FF0FFFFFE000007E6000001F0000000000FFFFFE0DE1F003F1FF8FFFFFE000003C7800001F0000000000FFFFFE0F81F803E1E00FFFFFE000003C7C00001F0000000000FFFFFE3F00F803E2790FFFFFC000003C7F00001F00000000007FFFFE3C00F803E00F1FFFFF8000003C7F80003F00000000003FFFFF3800F803E0031FFFFF0000003C7FC0003F00000000001FFFFF1000F807E0001FFFFE0000003C7FE0003F00000000000FFFFF0000FC07C0001FFFFC0000001E7FF0003F000000000007FFFF00007C07C0003FFFF80000001E7FF8003F000000000003FFFF80007C07C0003FFFF00000001E3FFC007F000000000001FFFF80007C07C0003FFFE00000001E1FFC007F000000000000FFFF80007C07C0003FFFC00000001E07FE007F0000000000007FFF80007C07C0003FFF800000001E01FE00770000000000003FFF80007E0FC0003FFF000000001E003F00770000000000001FFF80007E0F80007FFE000000000E001F00F70000000000000FFFC0003E0F80007FFC000000000E000F80E700000000000007FFC0003E0F80007FF8000000000E000780E700000000000003FFC0003E0F80007FF0000000000E000381E700000000000001FFC0003E0F80007FE0000000000F000381E70001F800000000FFC0003E0F80807FC0000000000F000181C70007FF000000007FC0203E0F80807F80000000000F000183C7800FFFC00000003FC0203E0F81C07F00000400000F0000C3C7801FFFF00000001FC0303E0F89C87E00000800000F0000C7C7803FFFFC0000000FC2723E0F8FF87C00000800000F0000C787807FFFFE00000007C3FE3E0F87F07E00001800000F00004F87807FFFFF8000000FC1FC3E0F83E07F00003000000F00001F8780F87FFFF800001FC0F83E0F83E07F80003000000F00001F03C0E007FFFFF8003FC0F83E0F83707FC0003000000F00003F03C18000FFFFF0007FC0CC3E0F84107FE0007000000F00007E03C100003FFFE000FFC1043E0F80007FF0007000000F0000FE03E000003FFFE001FFC0003E0F80007FF8007000000F0001FE03F00000FFFFC003FFC0003E0F80007FFC007000000F0003FC01F80007FFFF8007FFC0003E0F80007FFE007000001E0007F801FF80FFFFFF800FFFC0003E0FC0003FFF007800001E000FF801FFFFFFFF80001FFF80007E07C0003FFF807C00007C001FF000FFFFFFF800003FFF80007E07C0003FFFC07E0000F8007FE0007FFFFFC000007FFF80007C07C0003FFFE07FC007F800FFC0007FFFFE000000FFFF80007C07C0003FFFF03FFFFFF003FF80001FFFF0000001FFFF80007C07C0003FFFF83FFFFFE00FFF000007FF00000003FFFF80007C07C0001FFFFC1FFFFFC03FFC0000000000000007FFFF00007C07E0001FFFFE0FFFFF80FFF0000000000000000FFFFF0000FC03E0001FFFFF07FFFE03FFC0000000000000001FFFFF0000F803E0001FFFFF81FFFC3FFE00000000000000003FFFFF0000F803E0000FFFFFC07FE00F0000000000000000007FFFFE0000F803E0000FFFFFE0000000000000000000000000FFFFFE0000F803F0000FFFFFE0000000000000000000000000FFFFFE0001F801F0000FFFFFE0000000000000000000000000FFFFFE0001F001F00007FFFFE0000000000000000000000000FFFFFC0001F001F00007FFFFE000000E70180E004078000000FFFFFC0001F001F80007FFFFE000000660180600E030000000FFFFFC0003F000F80003FFFFE000000E603C0600E030000000FFFFF80003E000F80003FFFFE000000FE02C06016030000000FFFFF80003E000FC0001FFFFE000000E603C0601F030000000FFFFF00007E0007C0001FFFFE000000E606E06013030000000FFFFF00007C0007C0001FFFFE000000E60460E433832000000FFFFF00007C0007E0000FFFFE000000EF0CF0F83387E000000FFFFE0000FC0003E0000FFFFE0000000000000000000000000FFFFE0000F80003F00007FFFE0000000000000000000000000FFFFC0000F80003F00007FFFE0000000000000000000000000FFFFC0001F80001F00003FFFE0000000000000000000000000FFFF80001F00001F80003FFFE0000000000000000000000000FFFF80003F00000F80001FFFE0000000000000000000000000FFFF00003E00000FC0000FFFE0000000000000000000000000FFFE00007E000007C0000FFFE0000000000000000000000000FFFE00007E000007E00007FFE0000000000000000000000000FFFC0000FC000007E00007FFE0000000000000000000000000FFFC0000FC000003F00003FFE0000000000000000000000000FFF80001F8000003F00001FFE0000000000000000000000000FFF00001F8000001F80000FFE0000000000000000000000000FFE00003F0000001F80000FFE0000000000000000000000000FFE00003F0000000FC00007FE0000000000000000000000000FFC00007E00000007C00003FE0000000000000000000000000FF800007C00000007E00001FE0000000000000000000000000FF00000FC00000003F00001FE0000000000000000000000000FF00001F800000003F00000FFFFFFFFF80000000003FFFFFFFFE00001F800000001F800007FFFFFFFFC0000000007FFFFFFFFC00003F000000000FC00003FFFFFFFFE000000000FFFFFFFFF800007E000000000FC00001FFFFFFFFF000000001FFFFFFFFF000007E0000000007E00000FFFFFFFFF800000003FFFFFFFFE00000FC0000000003F000007FFFFFFFFC00000007FFFFFFFFC00001F80000000003F800003FFFFFFFFE0000000FFFFFFFFF800003F80000000001FC00001FFFFFFFFF0000001FFFFFFFFF000003F00000000000FC000007FFFFFFFF8000003FFFFFFFFC000007E000000000007E000003FFFFFFFFC000007FFFFFFFF800000FC000000000007F000001FFFFFFFFE00000FFFFFFFFF000001FC000000000003F8000007FFFFFFFF00001FFFFFFFFC000003F8000000000001FC000003FFFFFFFF80003FFFFFFFF8000007F0000000000000FE000001FFFFFFFFC0007FFFFFFFF000000FE00000000000007F0000007FFFFFFFE000FFFFFFFFC000001FC00000000000003F8000001FFFFFFFF001FFFFFFFF0000003F800000000000001FC000000FFFFFFFF803FFFFFFFE0000007F000000000000000FE0000003FFFFFFFC07FFFFFFF8000000FE0000000000000007F0000000FFFFFFFE0FFFFFFFE0000001FC0000000000000003F80000003FFFFFFF1FFFFFFF80000003F80000000000000001FC00000007FFFFFFBFFFFFFC00000007F00000000000000000FF00000001FFFFFFFFFFFFF00000001FE000000000000000007F800000603FFFFFFFFFFF800000003FC000000000000000003FC000004003FFFFFFFFF8000000007F8000000000000000001FE0000080003FFFFFFF8000000000FF0000000000000000000FF80001800000FFFFF80000700003FE00000000000000000003FC000100000000001840007C0007F800000000000000000001FF000300000000001C40004C001FF000000000000000000000FF800300000200000C6000FC003FE0000000000000000000003FE00380000700000C6000E000FF80000000000000000000001FF801F0000000000C2003C003FF000000000000000000000007FE007E0060040004301F800FFC000000000000000000000003FF801FC0606E000618FC003FF8000000000000000000000000FFE003FC607E00061FF000FFE00000000000000000000000003FF8047FC06200060F0003FF800000000000000000000000001FFF0780006300FE00000FFF0000000000000000000000000007FFC0800043FFFC00007FFC0000000000000000000000000001FFF8000180FF800003FFF000000000000000000000000000007FFF007F360000001FFFC000000000000000000000000000000FFFF01E36000001FFFE00000000000000000000000000000003FFFF800000003FFFF8000000000000000000000000000000007FFFFF00001FFFFFC0000000000000000000000000000000000FFFFFFFFFFFFFFE000000000000000000000000000000000001FFFFFFFFFFFFF00000000000000000000000000000000000001FFFFFFFFFFF0000000000000000000000000000000000000000FFFFFFFFE0000000000000000000000000000000000000000003FFFFF80000000000000000000000
        ^FO225,0^GB4,320,4^FS
        ^FO75,260^A0N,20,20^FDMS 1500^FS
        ^FO45,285^A0N,20,20^FD1 036-09/2020^FS
        ^FO240,25^A0N,26,26^FB390,1,0,L^FD${productName}^FS
        ^FO240,53^A0N,26,26^FB390,1,0,L^FD${productNameBm}^FS
        ^FO240,83^A0N,20,20^FB390,1,0,L^FDUOM Quantity : ${uomQuantity}^FS
        ^FO240,111^A0N,20,20^FB390,1,0,L^FDSKU : ${groupNumber}^FS
        ^FO240,139^A0N,20,20^FB390,1,0,L^FDProduction Date : ${productionDate}^FS
        ^FO240,167^A0N,20,20^FB390,1,0,L^FDExpiry Date : ${expiryDate}^FS
        ^FO240,201^A0N,20,20^FDManufactured by :^FS
        ^FO240,231^A0N,24,24^FDFARM TO FORK SDN BHD^FS
        ^FO240,266^A0N,15,15^FDNo 19 & 21,Desa Tun Razak Industrial Park,^FS
        ^FO240,291^A0N,15,15^FDCheras Jalan 13/118B Desa Tun Razak 56000 Kuala Lumpur^FS

        ^XZ`
    }

    @action createFactoryPrintZplManual = (print, printData) => {
        let printString = "";
        const labelPrinter = printData.printHalalLabel
            ? this.getManualFactoryHalalLabelPrintString.bind(this)
            : this.getManualFactoryLabelPrintString.bind(this);
    
        for (let i = 0; i < printData.total; i++) {
            printString += labelPrinter(printData);
        }
    
        if (print) {
            console.log(printString);
            if (PrintStore.isPrinterConnected) {
                PrintStore.writeToSelectedPrinter(printString);
            }
        }
    }

    @action createFactoryPrintZplAuto = (print) => {
        let printString = "";
    
        Object.keys(this.selectedSkusToPrint)
            .sort()
            .forEach(mealLetter => {
                const skus= this.selectedSkusToPrint[mealLetter];
                skus
                    .sort()
                    .forEach(sku => {
                        const skuData = this.productionBatches[mealLetter][sku]
                        const { 
                            mealComponentLine1,
                            mealComponentLine2,
                            totalPallets,
                            arrival_at,
                            expires_at,
                        } = skuData;
        
                        const productionDate = this.productionDate.format('DD/M')
                        const arrivalDateMoment = moment(arrival_at, 'x');
                        const expiryDateMoment = moment(expires_at, 'x').subtract(1, "days");
                        const arrivalDate = arrivalDateMoment.format('DD/M');
                        const expiryDate = expiryDateMoment.format('DD/M');
                        const batchDate = `${arrivalDate} - ${expiryDate}`;
        
                        const printData = {
                            batchDate,
                            mealComponentLine1,
                            mealComponentLine2,
                            productionDate,
                            sku,
                        }

                        printString += `
                        CT~~CD,~CC^~CT~
                        ^XA~TA000~JSN^LT0^MNW^MTT^PON^PMN^LH0,0^JMA^PR4,4~SD15^JUS^LRN^CI0^XZ
                        ^XA
                        ^MMT
                        ^PW1181
                        ^LL0591
                        ^LS0
                        ^FO512,32^GFA,01536,01536,00012,:Z64:eJztkzEOgzAQBC+icBPJP4CPIPEznKfxFJ5AmQKxuNrbNJEDlhBSrhpZ1s5tcWb3mqdwKzwov5zT5IyZ+MBCbvAmB6zkCCgztAMYOsh7kv/A5qoPZn4D9wb4PlnFPb+oCrQlql+1BarqDXGqoaoqNVTVmYaiqtVQVUcbmqjyf+dOribKNQVX5T2pyqHmswmPwr3957rZAVuQ0R4=:6616
                        ^FO576,32^GFA,01152,01152,00012,:Z64:eJzVkzEKxDAMBCVcuAn4B+en+GnJ0/KUe0LKK0I2rrQbSBEOEu5UDUbsWAKZ/WMNwi/hJjxOZMyBjndwEs5Yggs+wRUr47ExHmA8MMn7zH4KKigooCCDggQKOofAVQARjCJoIqgiKCLIDwhOp3HcvK5Lgl9c1yXB6TRfrCvYhU36u4Bn0OQ8qpxQ4X86B1rmdVhivDnjzRY71A7lmdnk:EFDB
                        ^FT241,464^A0N,208,206^FB703,1,0,C^FH\^FD${sku.replace(/_/g, '-')}^FS
                        ^FO580,46^GB0,170,18^FS
                        ^PQ1,0,1,Y^XZ
                        `
        
                        for (let i = 0; i < totalPallets; i++) {
                            printString += this.getFactoryLabelPrintString(printData);
                        }
                    })
            })

        if (print) {
            console.log(printString);
            PrintStore.writeToSelectedPrinter(printString);
        }
    }
}

const store = new FactoryStore();
export default store;
